import { getTeamsDateFormat, getTeamsTimeFormat } from "../utils/dateUtils";
import { CustomerMeeting } from "../../customerMeetings/store/CustomerMeeting";

const cs = {
    common: {
        newMeeting: "Nové setkání",
        errorMessage: (errorDetails: string) => `Nastala chyba: ${errorDetails}`,
        loader: "Načítání dat...",
    },
    appOutsideMsTeams: {
        message: "Aplikace musí být spuštěna v rámci Microsoft Teams",
    },
    logo: {
        corporation: "Logo mBank pro velkoobchodní klienty",
        retail: "Logo mBank pro maloobchodní klienty",
        private: "Logo mBank pro soukromé bankovnictví",
    },
    meetingModals: {
        closeButton: "Zavřít",
        startingHourLabel: "Začátek",
        endingHourLabel: "Konec",
        titlePlaceholder: "Přidat název",
        customerNamePlaceholder: "Imię i nazwisko klienta",
        customerEmailPlaceholder: "Adres e-mail klienta",
        customerIdPlaceholder: "ID klienta",
        additionalAttendeeEmailsPlaceholder: "E-mailové adresy dodatečných konzultantů",
        englishCommunicationCheckboxLabel: "Pošlete komunikaci v angličtině",
        addClient: "Dodaj klienta",
        add: "Dodaj",
        fullnamePlaceholder: "Imię i nazwisko",
        emailPlaceholder: "Adres e-mail",
        useVirtualAppointments: "Use Virtual Appointment",
        customers: "Klienci",
        consultants: "Dodatkowi pracownicy",
        externalConsultants: "Konsultanci zewnętrzni",
        thirdParties: "Osoby trzecie",
        consultantsToolip: "np. asystent/doradca/DR/DSM/BM/mTFI",
        externalConsultantsTooltip: "np. pracownicy kancelarii, z którymi mamy podpisane umowy",
        thirdPartiesTooltip: "np. prawnicy, mąż, sekretarka - osoby bez umocowania do rachunku",
        customersTooltip: "potencjalni klienci",
        validation: {
            invalidEmail: "E-mail je neplatný",
            invalidAttendees: "Jedna z uvedených e-mailových adres je neplatná",
            invalidRegon: "Uvedené číslo REGON je neplatné",
            invalidClientId: "Neplatný formát ID zákazníka",
            invalidC6: "Neplatný formát čísla C6",
            invalidTitle: "Obsah nesmí obsahovat následující znaky: <>",
            invalidSeparator: "Použijte středník jako oddělovač",
            invalidMeetingEndingDate: (maxNumberOfDays: number) =>
                `Datum ukončení schůze nesmí přesáhnout ${maxNumberOfDays} dnů`,
        },
        corporate: {
            addContactPerson: "Dodaj osobę kontaktową",
            companyNamePlaceholder: "Jméno společnosti",
            c6NumberPlaceholder: "Číslo C6",
            regonPlaceholder: "Číslo REGON (statistické číslo)",
        },
    },
    createMeeting: {
        header: "Nové setkání",
        selectBusinessLinePlaceholder: "Zvolte obchodní řadu",
        loadingBusinessLines: "Načítají se obchodní řady...",
        noBusinessLineFound: "Vytvoření schůzky není možné, protože nepatříte do žádné obchodní řady.",
        createButton: "Vytvořit schůzku",
        adding: "Probíhá vytváření schůzky...",
    },
    updateMeeting: {
        header: "Upravit schůzku",
        updateButton: "Upravit schůzku",
        updating: "Probíhá aktualizace schůzky...",
        newAdditionalAttendeeEmailsPlaceholder: "Dodatečné e-mailové adresy dodatečných konzultantů",
        save: "Uložit",
    },
    meetingsList: {
        noMeetings: "Neexistují žádné naplánované schůzky",
        headers: {
            date: "Datum schůzky",
            attendees: "Klienti",
            subject: "Téma setkání",
        },
        cancelActionButton: "Zrušit",
        cancelDialog: {
            message: (meeting: CustomerMeeting) =>
                `Opravdu chcete zrušit schůzku ${meeting.customers
                    .map((customer) => customer.name)
                    .join(", ")} (${getTeamsDateFormat(meeting.startAt)} v čase ${getTeamsTimeFormat(
                    meeting.startAt
                )} - ${getTeamsTimeFormat(meeting.endAt)})?`,
            additionalMessage: "Všichni účastníci setkání obdrží zprávu s informací o jeho zrušení.",
            header: "Chcete schůzku zrušit?",
            cancelButton: "Odejít",
            confirmButton: "Potvrdit",
        },
        updateButton: "Upravit",
        joinButton: "Zahajte schůzku",
        loading: "Probíhá načítání schůzek...",
        updateMeetingSuccessAlert: "Setkání bylo úspěšně aktulizováno",
        updateMeetingTypeButton: "Typ setkání",
    },
    businessLines: {
        private: "Soukromé",
        corporate: "Velkoobchod",
        retail: "Maloobchod",
    },
    organizerNoRecordingWarning: {
        content: (time: string) =>
            `Nahrávání bylo přerušeno. Zapněte je do ${time} sekund – pokud to neuděláte, schůzka bude automaticky ukončena.`,
    },
    inMeetingAppConfig: {
        header: "Setkání se zákazníky na dálku",
        info: "Aplikace slouží k zajištění procesu setkání se zákazníky na dálku",
    },
    beforeMeeting: {
        header: "Setkání se zákazníkem na dálku",
        organizatorSubHeader: "Jste pořadatelem této schůzky",
        presenterSubHeader: "Jste konzultantem na této schůzce",
        rulesHeader: "Před zahájením setkání se zákazníkem (vpuštěním zákazníka do místnosti)",
        setBackgroundRule: "Nastavte bankovní logo jako pozadí.",
        rememberHeader: "Pamatujte!",
        cautionHeader: "Upozornění!",
        notEnoughDiskSpace:
            "Nemáte dostatek místa na nahrání schůzky do OneDrive. Potřebujete 1 GB místa. Odstraňte nepotřebné soubory ze svého OneDrive.",
        meetingRules: {
            header: "Při schůzkách se zákazníky:",
            noDesktopSharingRule:
                "Nikdy nezpřístupňujte plochu. Můžete zpřístupnit pouze povolené aplikace uvedené v pracovních postupech.",
            noLinksOpenRule: "Neotevírejte žádné odkazy posílané přes chat.",
        },
        organizerRules: {
            recordRule: "Zahajte nahrávání schůzky.",
            alwaysRecordRule: "Vypnutí nahrávání schůzky způsobí její okamžité přerušení.",
        },
    },
    duringMeeting: {
        organizerView: {
            header: "Checklist schůzky",
            freeSpaceRequirement: "1 GB volného prostoru na OneDrive",
            isRecordingRequirement: "Pořadatel zapnul nahrávání",
            identityRequirement: "Potvrdit totožnost zákazníka",
        },
        presenterView: {
            header: "Jste konzultantem na této schůzce",
        },
    },
    organizerMeetingTypePrompt: {
        headerOnStartNotAnOrganizer: "Pořadatel vyplní údaje o schůzce",
        headerOnStartOrganizer: "Vyberte druh rozhovoru",
        headerOnFinish: "Děkujeme za vyplnění ankety",
        placeholderMeetingType: "Druh rozhovoru",
        save: "Uložit",
    },
    customerJoinsLobbyPrompt: {
        content:
            "Musíme nahrávat schůzky se zákazníky. ZAPNĚTE NAHRÁVÁNÍ, než se zákazník připojí ke schůzce. Pokud to neuděláte nebo nahrávání přerušíte - schůzka bude automaticky ukončena.",
    },
    screenSharingWarningPrompt: {
        defaultContent: "Nezapomínejte, že během rozhovoru můžete zpřístupnit pouze povolené soubory a aplikace.",
        privateContent:
            "Nezapomínejte, že během rozhovoru můžete zpřístupnit pouze povolené soubory a aplikace. Pokud chcete zpřístupnit Aplikaci poradce, nastavte v ní video režim.",
    },
    peoplePicker: {
        enterCharacters: "Zadejte alespoň 3 znaky",
        noResults: "Nenalezeny žádné výsledky",
        loading: "Vyhledávání",
        moreResults:
            "Znaleziono więcej wyników, spróbuj doprecyzować zapytanie jeśli lista nie zawiera szukanego elementu.",
    },
};

export default cs;
